import { ChakraProvider, createStandaloneToast } from '@chakra-ui/react';
import { ErrorBoundary } from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { Router } from 'react-router-dom';

import App from './App';
import ScrollToTop from './ScrollToTop';
import customTheme from './chakra';
import ReCaptcha from './components/ReCaptcha';
import Fonts from './components/ui/Fonts';
import config, { isProd } from './config';
import browserHistory from './history';
import ErrorPage from './pages/ErrorPage';
import AuthorizedApolloProvider from './providers/AuthorizedApolloProvider';
import { initializeTagManager } from './services/analytics/googleTagManager';
import hotjar, { HotjarEvents } from './services/analytics/hotjar';
import { RudderEvent, rudderanalytics } from './services/analytics/rudderstack';
import queryClient from './services/marketplace/queryClient';
import { initSentry } from './services/sentry';
import { captureClickIds } from './utils/clickIds';
import { hasPrivacySignal } from './utils/privacy';
import { resolveVisitorCountry } from './utils/visitorGeo';

if (!hasPrivacySignal()) {
  initializeTagManager();
}
captureClickIds(window.location.search);

const { ToastContainer } = createStandaloneToast({
  theme: customTheme,
});
const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

initSentry();

(async () => {
  const country = await resolveVisitorCountry();
  const LDProvider = await asyncWithLDProvider({
    clientSideID: config.launchDarklyClientId,
    context: {
      kind: 'user',
      anonymous: true,
      ...(country ? { country } : {}),
    },
    options: {
      streaming: false,
    },
    reactOptions: {
      useCamelCaseFlagKeys: false,
    },
  });

  const onError = (e: Error) => {
    if (!isProd) {
      // eslint-disable-next-line no-console
      console.log(e);
    } else {
      rudderanalytics.track(RudderEvent.Error, {
        error_message: e.message,
        pathname: window.location.pathname,
      });
      hotjar.track(HotjarEvents.ReactError);
    }
  };

  root.render(
    <React.StrictMode>
      <ErrorBoundary fallback={ErrorPage} onError={onError}>
        <Suspense>
          <Router history={browserHistory}>
            <AuthorizedApolloProvider>
              <QueryClientProvider client={queryClient}>
                <LDProvider>
                  <ChakraProvider theme={customTheme}>
                    <Fonts />
                    <ScrollToTop />
                    <ReCaptcha />
                    <App />
                    <ToastContainer />
                  </ChakraProvider>
                </LDProvider>
              </QueryClientProvider>
            </AuthorizedApolloProvider>
          </Router>
        </Suspense>
      </ErrorBoundary>
    </React.StrictMode>,
  );
})();
